import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Download, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API_URL } from './config';

export const GestionContratos = ({ playlist, API: propAPI }) => {
  const API = propAPI || API_URL;
  const [contratos, setContratos] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  // Feb-2026: catálogo de precios para autocompletar cuando se crea un
  // contrato sin presupuesto pero el evento tiene `servicios_contratados`.
  const [precios, setPrecios] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    cliente_nombre: '',
    cliente_dni: '',
    cliente_direccion: '',
    cliente_email: '',
    cliente_telefono: '',
    servicios: [],
    subtotal: 0,
    descuento_porcentaje: 0,
    total_descuento: 0,
    base_imponible: 0,
    retencion_irpf: 0,
    total_retencion: 0,
    iva: 10,
    total_iva: 0,
    precio_total: 0,
    anticipo: 0,
    resto: 0,
    condiciones_pago: 'Anticipo del 30% al confirmar reserva. Resto 7 días antes del evento.'
  });
  const [nuevoServicio, setNuevoServicio] = useState({
    nombre: '',
    descripcion: '',
    precio: 0
  });

  useEffect(() => {
    loadContratos();
    loadPresupuestos();
    loadPrecios();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: loadContratos and loadPresupuestos
  }, [playlist]);

  const loadContratos = async () => {
    try {
      const response = await axios.get(`${API}/playlists/${playlist.id}/contratos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setContratos(response.data.contratos || []);
    } catch (error) {
      console.error('Error loading contratos:', error);
    }
  };

  const loadPresupuestos = async () => {
    try {
      console.log('Loading presupuestos for playlist:', playlist.id, 'API:', API);
      const response = await axios.get(`${API}/playlists/${playlist.id}/presupuestos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('Presupuestos loaded:', response.data);
      setPresupuestos(response.data || []);
    } catch (error) {
      console.error('Error loading presupuestos:', error);
    }
  };

  const loadPrecios = async () => {
    // Feb-2026: cargar catálogo de precios para autocompletar servicios
    // cuando se genera contrato manual desde servicios_contratados del evento.
    try {
      const response = await axios.get(`${API}/precios`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setPrecios(response.data?.precios || response.data || []);
    } catch (error) {
      console.error('Error loading precios (Fix Bug 4):', error);
      // No es bloqueante: si falla, los precios quedan en 0 y el admin los rellena.
    }
  };

  const generarContratoDesdePresupuesto = (presupuesto) => {
    console.log('Generando contrato desde presupuesto:', presupuesto);

    // Feb-2026 FIX Bug 1: respetar `cantidad` del servicio. Antes se copiaba
    // solo `s.precio` unitario y se ignoraba `s.cantidad`, provocando
    // subtotales por debajo del real (ej. Barra libre 100€ × 3 → 100€).
    const serviciosArray = presupuesto.servicios.map(s => {
      const cantidad = parseFloat(s.cantidad) || 1;
      const precioUnitario = parseFloat(s.precio) || 0;
      return {
        nombre: (s.nombre || '') + (cantidad > 1 ? ` (x${cantidad})` : ''),
        descripcion: s.descripcion || '',
        precio: precioUnitario * cantidad, // precio TOTAL del servicio
      };
    });

    console.log('Servicios extraídos:', serviciosArray);

    // Calculate subtotal from services (con cantidad ya aplicada arriba)
    const subtotal = serviciosArray.reduce((sum, s) => sum + (parseFloat(s.precio) || 0), 0);

    // Get financial data from presupuesto (con guards defensivos)
    const descuento_porcentaje = parseFloat(presupuesto.descuento_porcentaje) || 0;
    const total_descuento = parseFloat(presupuesto.total_descuento) || (subtotal * descuento_porcentaje / 100);
    const base_imponible = parseFloat(presupuesto.base_imponible) || (subtotal - total_descuento);
    const retencion_irpf = parseFloat(presupuesto.retencion_irpf) || 0;
    const total_retencion = parseFloat(presupuesto.total_retencion) || (base_imponible * retencion_irpf / 100);
    const iva = presupuesto.iva !== undefined && presupuesto.iva !== null ? parseFloat(presupuesto.iva) : 10;
    const total_iva = parseFloat(presupuesto.total_iva) || (base_imponible * iva / 100);
    const precio_total = parseFloat(presupuesto.total) || (base_imponible - total_retencion + total_iva);

    const newFormData = {
      cliente_nombre: presupuesto.cliente_nombre || playlist.nombre_novios || '',
      cliente_dni: presupuesto.cliente_dni || '',
      cliente_direccion: presupuesto.cliente_direccion || '',
      cliente_email: presupuesto.cliente_email || playlist.email_novios || '',
      cliente_telefono: presupuesto.cliente_telefono || playlist.telefono_novios || '',
      servicios: serviciosArray,
      subtotal: subtotal,
      descuento_porcentaje: descuento_porcentaje,
      total_descuento: total_descuento,
      base_imponible: base_imponible,
      retencion_irpf: retencion_irpf,
      total_retencion: total_retencion,
      iva: iva,
      total_iva: total_iva,
      precio_total: precio_total,
      anticipo: parseFloat(presupuesto.anticipo) || Math.round(precio_total * 0.3),
      resto: parseFloat(presupuesto.resto) || Math.round(precio_total * 0.7),
      condiciones_pago: 'Anticipo del 30% al confirmar reserva. Resto 7 días antes del evento.',
      // Feb-2026 FIX Bug 3: recordar el presupuesto de origen
      presupuesto_id: presupuesto.id || null,
    };

    console.log('Form data generado:', newFormData);
    setFormData(newFormData);
    setShowForm(true);
  };

  const handleNuevoContrato = () => {
    // Check if there's a presupuesto to use
    if (presupuestos.length > 0) {
      const ultimoPresupuesto = presupuestos[presupuestos.length - 1];
      generarContratoDesdePresupuesto(ultimoPresupuesto);
    } else {
      // Manual form with basic playlist data
      const serviciosContratados = playlist.servicios_contratados || [];
      // Feb-2026 FIX Bug 4: cruzar con catálogo `precios` para autocompletar
      // el precio base cuando el nombre del servicio coincida (case-insensitive).
      const preciosLookup = new Map(
        (precios || []).map(p => [(p.nombre || '').trim().toLowerCase(), parseFloat(p.precio_base) || 0])
      );
      const serviciosArray = serviciosContratados.map(s => {
        const key = (s.nombre || '').trim().toLowerCase();
        return {
          nombre: s.nombre || '',
          descripcion: s.notas || '',
          precio: preciosLookup.get(key) || 0, // 0 si no hay match; admin puede editar
        };
      });

      // Feb-2026 FIX Bug 2: inicializar TODOS los campos numéricos con 0
      // para evitar undefined → NaN€ en calcularTotales.
      const subtotal = serviciosArray.reduce((sum, s) => sum + (parseFloat(s.precio) || 0), 0);
      setFormData({
        cliente_nombre: playlist.nombre_novios || '',
        cliente_dni: '',
        cliente_direccion: '',
        cliente_email: playlist.email_novios || '',
        cliente_telefono: playlist.telefono_novios || '',
        servicios: serviciosArray,
        subtotal: subtotal,
        descuento_porcentaje: 0,
        total_descuento: 0,
        base_imponible: subtotal,
        retencion_irpf: 0,
        total_retencion: 0,
        iva: 10,
        total_iva: subtotal * 0.10,
        precio_total: subtotal * 1.10,
        anticipo: Math.round(subtotal * 1.10 * 0.3),
        resto: Math.round(subtotal * 1.10 * 0.7),
        condiciones_pago: 'Anticipo del 30% al confirmar reserva. Resto 7 días antes del evento.',
        presupuesto_id: null,
      });
      setShowForm(true);
    }
  };

  const handleAddServicio = () => {
    if (nuevoServicio.nombre && nuevoServicio.precio > 0) {
      setFormData(prev => ({
        ...prev,
        servicios: [...prev.servicios, { ...nuevoServicio }]
      }));
      setNuevoServicio({ nombre: '', descripcion: '', precio: 0 });
    }
  };

  const handleRemoveServicio = (index) => {
    setFormData(prev => ({
      ...prev,
      servicios: prev.servicios.filter((_, i) => i !== index)
    }));
  };

  const calcularTotales = () => {
    // Feb-2026 FIX Bug 2: guards defensivos contra undefined/NaN.
    // Antes: formData.descuento_porcentaje podía ser undefined → NaN se
    // propagaba a todos los importes ("NaN€" en pantalla).
    const subtotal = formData.servicios.reduce((sum, s) => sum + (parseFloat(s.precio) || 0), 0);
    const pctDescuento = parseFloat(formData.descuento_porcentaje) || 0;
    const pctIrpf = parseFloat(formData.retencion_irpf) || 0;
    const pctIva = parseFloat(formData.iva) || 0;
    const descuento = subtotal * (pctDescuento / 100);
    const baseImponible = subtotal - descuento;
    const retencion = baseImponible * (pctIrpf / 100);
    const iva = baseImponible * (pctIva / 100);
    const total = baseImponible - retencion + iva;

    return {
      subtotal,
      total_descuento: descuento,
      base_imponible: baseImponible,
      total_retencion: retencion,
      total_iva: iva,
      precio_total: total,
    };
  };

  const handleSubmit = async () => {
    try {
      const totales = calcularTotales();
      const anticipo = parseFloat(formData.anticipo) || 0;
      
      const contratoData = {
        playlist_id: playlist.id,
        // Feb-2026 FIX Bug 3: enviar presupuesto_id si el contrato viene de uno
        presupuesto_id: formData.presupuesto_id || null,
        cliente_nombre: formData.cliente_nombre,
        cliente_dni: formData.cliente_dni,
        cliente_direccion: formData.cliente_direccion,
        cliente_email: formData.cliente_email,
        cliente_telefono: formData.cliente_telefono,
        servicios: formData.servicios,
        subtotal: totales.subtotal,
        descuento_porcentaje: formData.descuento_porcentaje,
        total_descuento: totales.total_descuento,
        base_imponible: totales.base_imponible,
        retencion_irpf: formData.retencion_irpf,
        total_retencion: totales.total_retencion,
        iva: formData.iva,
        total_iva: totales.total_iva,
        precio_total: totales.precio_total,
        anticipo: anticipo,
        resto: totales.precio_total - anticipo,
        condiciones_pago: formData.condiciones_pago
      };

      await axios.post(`${API}/contratos`, contratoData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      alert('Contrato generado exitosamente');
      setShowForm(false);
      loadContratos();
      
      // Reset form (Feb-2026 FIX Bug 2: inicializar TODOS los campos numéricos
      // para no volver a caer en undefined → NaN€ en el próximo `handleNuevoContrato`).
      setFormData({
        cliente_nombre: '',
        cliente_dni: '',
        cliente_direccion: '',
        cliente_email: '',
        cliente_telefono: '',
        servicios: [],
        subtotal: 0,
        descuento_porcentaje: 0,
        total_descuento: 0,
        base_imponible: 0,
        retencion_irpf: 0,
        total_retencion: 0,
        iva: 10,
        total_iva: 0,
        precio_total: 0,
        anticipo: 0,
        resto: 0,
        condiciones_pago: 'Anticipo del 30% al confirmar reserva. Resto 7 días antes del evento.',
        presupuesto_id: null,
      });
    } catch (error) {
      console.error('Error creating contrato:', error);
      alert('Error al generar el contrato');
    }
  };

  const handleDownloadPDF = (contratoId) => {
    window.open(`${API}/contratos/${contratoId}/pdf`, '_blank');
  };

  const handleFirmarContratoAdmin = async (contratoId) => {
    if (!window.confirm('¿Deseas firmar este contrato como administrador?')) {
      return;
    }
    
    try {
      await axios.post(`${API}/contratos/${contratoId}/firmar-admin`, 
        {
          acepto_terminos: true,
          firmante_nombre: "Admin"
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      alert('Contrato firmado exitosamente');
      loadContratos();
    } catch (error) {
      console.error('Error signing contract:', error);
      alert(error.response?.data?.detail || 'Error al firmar el contrato');
    }
  };

  const handleDeleteContrato = async (contratoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este contrato? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/contratos/${contratoId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      alert('Contrato eliminado exitosamente');
      loadContratos();
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert('Error al eliminar el contrato');
    }
  };

  return (
    <Card className="bg-slate-800 border-slate-700 mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" />
            Gestión de Contratos
          </CardTitle>
          <Button
            onClick={handleNuevoContrato}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            {showForm ? 'Cancelar' : 'Nuevo Contrato'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm ? (
          <div className="space-y-6 p-4 bg-slate-900/50 rounded-lg">
            {/* Presupuestos disponibles */}
            {presupuestos.length > 0 && (
              <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                <h4 className="text-blue-300 font-semibold mb-2">💡 Presupuestos Disponibles</h4>
                <p className="text-slate-400 text-sm mb-3">
                  Puedes usar un presupuesto existente como base para el contrato:
                </p>
                <div className="space-y-2">
                  {presupuestos.map((pres) => (
                    <div key={pres.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                      <div>
                        <p className="text-white text-sm font-medium">{pres.cliente_nombre}</p>
                        <p className="text-slate-400 text-xs">
                          {pres.servicios.length} servicios • Total: {pres.precio_total}€
                        </p>
                      </div>
                      <Button
                        onClick={() => generarContratoDesdePresupuesto(pres)}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Usar este
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <h3 className="text-white font-semibold text-lg mb-4">Generar Nuevo Contrato</h3>
            
            {/* Datos del cliente */}
            <div className="space-y-4">
              <h4 className="text-slate-300 font-semibold">Datos del Cliente</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Nombre Completo *</Label>
                  <Input
                    value={formData.cliente_nombre}
                    onChange={(e) => setFormData({ ...formData, cliente_nombre: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">DNI/NIE *</Label>
                  <Input
                    value={formData.cliente_dni}
                    onChange={(e) => setFormData({ ...formData, cliente_dni: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={formData.cliente_email}
                    onChange={(e) => setFormData({ ...formData, cliente_email: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Teléfono</Label>
                  <Input
                    value={formData.cliente_telefono}
                    onChange={(e) => setFormData({ ...formData, cliente_telefono: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-slate-300">Dirección</Label>
                  <Input
                    value={formData.cliente_direccion}
                    onChange={(e) => setFormData({ ...formData, cliente_direccion: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Servicios */}
            <div className="space-y-4">
              <h4 className="text-slate-300 font-semibold">Servicios Contratados</h4>
              
              {/* Servicios actuales */}
              {formData.servicios.length > 0 && (
                <div className="space-y-2">
                  {formData.servicios.map((servicio, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-800 rounded">
                      <div className="flex-1">
                        <p className="text-white font-medium">{servicio.nombre}</p>
                        {servicio.descripcion && (
                          <p className="text-slate-400 text-sm">{servicio.descripcion}</p>
                        )}
                      </div>
                      <p className="text-cyan-400 font-semibold">{servicio.precio}€</p>
                      <button
                        onClick={() => handleRemoveServicio(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Añadir servicio */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <Input
                    placeholder="Nombre del servicio"
                    value={nuevoServicio.nombre}
                    onChange={(e) => setNuevoServicio({ ...nuevoServicio, nombre: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="md:col-span-4">
                  <Input
                    placeholder="Descripción (opcional)"
                    value={nuevoServicio.descripcion}
                    onChange={(e) => setNuevoServicio({ ...nuevoServicio, descripcion: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="md:col-span-3">
                  <Input
                    type="number"
                    placeholder="Precio"
                    value={nuevoServicio.precio || ''}
                    onChange={(e) => setNuevoServicio({ ...nuevoServicio, precio: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="md:col-span-1">
                  <Button
                    onClick={handleAddServicio}
                    className="w-full bg-cyan-600 hover:bg-cyan-700"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Totales y Conceptos Financieros */}
            <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg">
              <h4 className="text-slate-300 font-semibold">Desglose Económico</h4>
              
              {/* Subtotal */}
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Subtotal (servicios):</span>
                <span className="text-white">{calcularTotales().subtotal.toFixed(2)}€</span>
              </div>
              
              {/* Descuento */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-sm">Descuento (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.descuento_porcentaje || ''}
                    onChange={(e) => setFormData({ ...formData, descuento_porcentaje: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div className="flex items-end">
                  <span className="text-red-400 pb-2">-{calcularTotales().total_descuento.toFixed(2)}€</span>
                </div>
              </div>
              
              {/* Base Imponible */}
              <div className="flex items-center justify-between border-t border-slate-700 pt-2">
                <span className="text-slate-300">Base Imponible:</span>
                <span className="text-white font-medium">{calcularTotales().base_imponible.toFixed(2)}€</span>
              </div>
              
              {/* IRPF */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-sm">Retención IRPF (%)</Label>
                  <select
                    value={formData.retencion_irpf}
                    onChange={(e) => setFormData({ ...formData, retencion_irpf: parseFloat(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded px-3 py-2"
                  >
                    <option value="0">0% (Sin retención)</option>
                    <option value="7">7% (Nuevo autónomo)</option>
                    <option value="15">15% (General)</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <span className="text-orange-400 pb-2">-{calcularTotales().total_retencion.toFixed(2)}€</span>
                </div>
              </div>
              
              {/* IVA */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400 text-sm">IVA (%)</Label>
                  <select
                    value={formData.iva}
                    onChange={(e) => setFormData({ ...formData, iva: parseFloat(e.target.value) })}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded px-3 py-2"
                  >
                    <option value="0">0% (Exento)</option>
                    <option value="10">10% (Reducido)</option>
                    <option value="21">21% (General)</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <span className="text-cyan-400 pb-2">+{calcularTotales().total_iva.toFixed(2)}€</span>
                </div>
              </div>
              
              {/* Total Final */}
              <div className="flex items-center justify-between border-t border-slate-600 pt-3 text-lg">
                <span className="text-white font-semibold">TOTAL:</span>
                <span className="text-emerald-400 font-bold text-xl">{calcularTotales().precio_total.toFixed(2)}€</span>
              </div>
              
              {/* Anticipo y Resto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-slate-700">
                <div>
                  <Label className="text-slate-300">Anticipo</Label>
                  <Input
                    type="number"
                    value={formData.anticipo || ''}
                    onChange={(e) => setFormData({ ...formData, anticipo: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Resto a pagar</Label>
                  <Input
                    type="number"
                    value={(calcularTotales().precio_total - (parseFloat(formData.anticipo) || 0)).toFixed(2)}
                    disabled
                    className="bg-slate-800 border-slate-700 text-slate-400"
                  />
                </div>
              </div>
            </div>

            {/* Condiciones de pago */}
            <div>
              <Label className="text-slate-300">Condiciones de Pago</Label>
              <Textarea
                value={formData.condiciones_pago}
                onChange={(e) => setFormData({ ...formData, condiciones_pago: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
                rows={3}
              />
            </div>

            {/* Botones */}
            <div className="flex gap-3">
              <Button
                onClick={handleSubmit}
                disabled={!formData.cliente_nombre || !formData.cliente_dni || formData.servicios.length === 0}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <FileText className="w-4 h-4 mr-2" />
                Generar Contrato
              </Button>
              <Button
                onClick={() => setShowForm(false)}
                variant="outline"
                className="border-slate-600 text-slate-300"
              >
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {contratos.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No hay contratos generados para este evento</p>
                <p className="text-slate-500 text-sm mt-2">Haz clic en "Nuevo Contrato" para crear uno</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contratos.map((contrato) => (
                  <Card key={contrato.id} className={`border-2 ${
                    contrato.estado === 'firmado' 
                      ? 'bg-green-900/10 border-green-700/50' 
                      : 'bg-slate-700/30 border-slate-600'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-white font-semibold">
                              Contrato #{contrato.id.slice(0, 8).toUpperCase()}
                            </h4>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              contrato.estado === 'firmado_ambos' 
                                ? 'bg-green-600 text-white' 
                                : contrato.estado === 'firmado_cliente'
                                ? 'bg-blue-600 text-white'
                                : 'bg-orange-600 text-white'
                            }`}>
                              {contrato.estado === 'firmado_ambos' ? '✓✓ Firmado Completo' : 
                               contrato.estado === 'firmado_cliente' ? '✓ Cliente Firmado' : 
                               'Pendiente Firmas'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-slate-400">Cliente:</span>
                              <p className="text-white">{contrato.cliente_nombre}</p>
                            </div>
                            <div>
                              <span className="text-slate-400">Total:</span>
                              <p className="text-cyan-400 font-semibold">{contrato.precio_total}€</p>
                            </div>
                            <div>
                              <span className="text-slate-400">Generado:</span>
                              <p className="text-white">{new Date(contrato.fecha_generacion).toLocaleDateString('es-ES')}</p>
                            </div>
                            {contrato.estado === 'firmado' && contrato.fecha_firma && (
                              <div>
                                <span className="text-slate-400">Firmado:</span>
                                <p className="text-green-400">{new Date(contrato.fecha_firma).toLocaleDateString('es-ES')}</p>
                              </div>
                            )}
                            {contrato.fecha_firma_cliente && (
                              <div>
                                <span className="text-slate-400">Firmado Cliente:</span>
                                <p className="text-green-400 text-xs">{new Date(contrato.fecha_firma_cliente).toLocaleDateString('es-ES')}</p>
                              </div>
                            )}
                            {contrato.fecha_firma_admin && (
                              <div>
                                <span className="text-slate-400">Firmado Admin:</span>
                                <p className="text-green-400 text-xs">{new Date(contrato.fecha_firma_admin).toLocaleDateString('es-ES')}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => handleDownloadPDF(contrato.id)}
                            size="sm"
                            className="bg-sky-600 hover:bg-sky-700"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            PDF
                          </Button>
                          {!contrato.fecha_firma_admin && (
                            <Button
                              onClick={() => handleFirmarContratoAdmin(contrato.id)}
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Firmar
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDeleteContrato(contrato.id)}
                            size="sm"
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Borrar
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
